import { forwardRef, useCallback, useRef, useState } from "react";
import { useEventListener } from "usehooks-ts";

import { ImpersonateUserModal } from "@/components/user/UserActions";
import {
  useCurrentOrganization,
  useCurrentPlatform,
  useCurrentUser,
  useSwitchOrganization,
  useSwitchPlatform,
} from "@/contexts";
import { env } from "@/env";
import {
  useAddLeagueNamingKey,
  useAddTournamentNamingKey,
  useCanImpersonateUsers,
  useScopedHref,
} from "@/hooks";
import { useT } from "@/i18n";
import { prefetchPage } from "@/routes/prefetch";
import {
  AsyncCommand,
  cn,
  IconEdit,
  IconArrowLink,
  IconEnter,
  IconEye,
  IconTrophy,
  IconWallet,
  type OptionAction,
} from "@/ui";
import { buildScopedPath } from "@/utils/common/scoped-routes";

import { GlobalSearchItem, UserSearchItem } from "./GlobalSearch.types";
import {
  SearchItemView,
  SUPER_ADMIN_FILTERS,
  useGetSearchItemTarget,
  useOpenSearchItemTarget,
} from "./SearchItemView";
import { SuperAdminModeButton } from "./SuperAdminModeButton";
import { useGlobalSearch } from "./useGlobalSearch";
import { useGlobalSearchAnalytics } from "./useGlobalSearchAnalytics";
import { useGlobalSearchMode } from "./useGlobalSearchMode";

export type GlobalSearchProps = {
  className?: string;
};

export const GlobalSearch = forwardRef<HTMLInputElement, GlobalSearchProps>(
  ({ className }, ref) => {
    const analytics = useGlobalSearchAnalytics();
    const lastItemsRef = useRef<GlobalSearchItem[]>([]);
    // Flag set right before we programmatically focus from Cmd/Ctrl+K so the
    // subsequent `focus` event can attribute the open to the shortcut instead
    // of a plain click/tab.
    const shortcutTriggerRef = useRef(false);
    // The input blurs as soon as a row is clicked. We defer the close-on-blur
    // emission by one frame so the selection click can flip the session's
    // outcome to "selected" first.
    const blurCloseTimerRef = useRef<number | null>(null);

    const handleQueryExecuted = useCallback(
      (props: {
        keyword: string;
        page: number;
        resultCount: number;
        items: GlobalSearchItem[];
        latencyMs: number;
      }) => {
        lastItemsRef.current = props.items;
        analytics.recordQueryExecuted(props);
      },
      [analytics]
    );

    const { mode, canUseSuperAdminMode, isSuperAdminMode, toggleSuperAdminMode } =
      useGlobalSearchMode();

    const {
      loadOptions,
      getOptionLabel,
      getOptionKey,
      resetKey,
      recentItems,
      addRecentItem,
      isShowingRecents,
    } = useGlobalSearch({ mode, onQueryExecuted: handleQueryExecuted });
    const addLeagueNamingKey = useAddLeagueNamingKey();
    const addTournamentNamingKey = useAddTournamentNamingKey();
    const t = useT();
    const switchPlatform = useSwitchPlatform();
    const switchOrganization = useSwitchOrganization();
    const currentPlatform = useCurrentPlatform();
    const currentOrganization = useCurrentOrganization();
    const currentUser = useCurrentUser();
    // Impersonation is superAdmin-only and hidden inside an impersonated tab —
    // the same gate the users-table row action uses. See docs/impersonation.md.
    const canImpersonateUser = useCanImpersonateUsers();
    // The modal is rendered at this component's root so it
    // survives the search popover closing on action.
    const [impersonateTarget, setImpersonateTarget] = useState<UserSearchItem["user"] | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const getSearchItemTarget = useGetSearchItemTarget(mode);
    const openSearchItemTarget = useOpenSearchItemTarget();
    const scopedHref = useScopedHref();

    const label = t("general.globalSearch.label.base", {
      tournaments: t(addTournamentNamingKey("general.globalSearch.label.tournaments")),
      leagues: t(addLeagueNamingKey("general.globalSearch.label.leagues")),
      venues: t("general.globalSearch.label.venues"),
    });

    useEventListener("keydown", (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        shortcutTriggerRef.current = true;
        searchInputRef.current?.focus();
      }
    });

    // Warm the chunks the user is about to navigate to. Fired the first time
    // they engage the input (focus / type) — strong signal they're about to
    // pick a result, plus avoids prefetching on every dashboard page mount.
    // The underlying prefetchers are idempotent so attaching with the default
    // (non-`once`) listener still costs at most one fetch per chunk.
    //
    // Covers every navigation target reachable from the popover:
    //  - list pages with `?search=<id>` deep-link (user / tournament / club / team)
    //  - league + license edit pages
    //  - purchased-licenses page (license + user secondary actions)
    const prefetchSearchTargets = () => {
      prefetchPage.usersList();
      prefetchPage.tournamentsList();
      prefetchPage.clubsList();
      prefetchPage.teamsList();
      prefetchPage.leagueEditLegacy();
      prefetchPage.licenseEdit();
      prefetchPage.purchasedLicense();
      prefetchPage.userEdit();
    };

    const handleFocus = useCallback(() => {
      prefetchSearchTargets();
      const trigger = shortcutTriggerRef.current ? "shortcut_cmd_k" : "focus";
      shortcutTriggerRef.current = false;
      analytics.open(trigger);
      if (blurCloseTimerRef.current !== null) {
        window.clearTimeout(blurCloseTimerRef.current);
        blurCloseTimerRef.current = null;
      }
    }, [analytics]);

    const handleBlur = useCallback(() => {
      // Defer so a click-on-row that follows the blur can call
      // `onOptionAction` first and mark the session selected.
      if (blurCloseTimerRef.current !== null) {
        window.clearTimeout(blurCloseTimerRef.current);
      }
      blurCloseTimerRef.current = window.setTimeout(() => {
        analytics.close();
        blurCloseTimerRef.current = null;
      }, 150);
    }, [analytics]);

    useEventListener("focus", handleFocus, searchInputRef);
    useEventListener("blur", handleBlur, searchInputRef);

    // Per-row submenu (the chevron at the row's trailing edge). Returns the
    // shortcut actions for a given entity, or `undefined` to suppress the
    // chevron entirely for rows that don't have shortcuts (pages, platforms,
    // organizations, and other rows without useful shortcuts.
    const getOptionActions = (option: GlobalSearchItem): OptionAction[] | undefined => {
      const trackAction = (actionKey: string) => () => {
        analytics.recordSecondaryAction({
          option,
          items: lastItemsRef.current,
          actionKey,
        });
      };
      switch (option.type) {
        case "tournament": {
          if (option.internalId == null) return undefined;
          const publicHref = `${env.clientUrl.replace(/\/+$/, "")}/tournament/${option.internalId}`;
          // No "Manager view" shortcut here: selecting the row itself already
          // opens Manager in a new tab (see useGetSearchItemTarget).
          return [
            {
              key: "publicView",
              label: t("general.form.preview.label"),
              icon: IconArrowLink,
              onAction: () => {
                trackAction("publicView")();
                window.open(publicHref, "_blank", "noopener,noreferrer");
              },
            },
          ];
        }
        case "user": {
          const actions: OptionAction[] = [
            {
              key: "edit",
              label: t("general.actions.edit"),
              href: scopedHref(`/users/edit/${option.user.id}`),
              icon: IconEdit,
              onAction: trackAction("edit"),
            },
            {
              key: "viewOrganizedTournaments",
              label: t("general.globalSearch.user.actions.viewOrganizedTournaments"),
              href:
                mode === "superAdmin"
                  ? scopedHref(`/tournaments?organizerIds=${option.user.id}&${SUPER_ADMIN_FILTERS}`)
                  : scopedHref(`/tournaments?organizerIds=${option.user.id}`),
              icon: IconTrophy,
              onAction: trackAction("viewOrganizedTournaments"),
            },
            {
              key: "viewPurchasedLicenses",
              label: t("general.globalSearch.user.actions.viewPurchasedLicenses"),
              href: scopedHref(`/issued-licenses?holderUserIds=${option.user.id}`),
              icon: IconWallet,
              onAction: trackAction("viewPurchasedLicenses"),
            },
          ];
          // Never offer impersonation on your own row. Opens the same
          // confirmation modal as the users table.
          if (canImpersonateUser && option.user.id !== currentUser.id) {
            actions.push({
              key: "impersonate",
              label: t("user.actions.impersonate"),
              icon: IconEye,
              onAction: () => {
                trackAction("impersonate")();
                setImpersonateTarget(option.user);
              },
            });
          }
          return actions;
        }
        case "license":
          return [
            {
              key: "viewPurchased",
              label: t("general.globalSearch.license.actions.viewPurchased"),
              href: scopedHref(`/issued-licenses?licenseIds=${option.license.id}`),
              icon: IconWallet,
              onAction: trackAction("viewPurchased"),
            },
          ];
        case "platform": {
          const isCurrentPlatform = option.platform.id === currentPlatform.id;
          const actions: OptionAction[] = [
            {
              key: "edit",
              label: t("general.actions.edit"),
              // Editing the current platform stays in the current scope; editing another
              // platform switches INTO it. The platform search option carries no owner, so
              // the entity segment falls back to the current organization.
              href: isCurrentPlatform
                ? scopedHref("/platforms/settings/current")
                : buildScopedPath(
                    option.platform.id,
                    currentOrganization?.id ?? option.platform.id,
                    null,
                    "platforms/settings/current"
                  ),
              icon: IconEdit,
              onAction: trackAction("edit"),
            },
          ];
          // You can't switch to the platform you're already in — omit the action.
          if (!isCurrentPlatform) {
            actions.push({
              key: "switch",
              label: t("general.globalSearch.actions.switchPlatform"),
              icon: IconEnter,
              onAction: () => {
                trackAction("switch")();
                switchPlatform(option.platform.id);
              },
            });
          }
          return actions;
        }
        case "organization":
          if (mode === "superAdmin") return undefined;
          return [
            {
              key: "edit",
              label: t("general.actions.edit"),
              // Switch the entity scope to that organization within the current platform.
              href: buildScopedPath(
                currentPlatform.id,
                option.organization.id,
                null,
                "entities/edit/current"
              ),
              icon: IconEdit,
              onAction: trackAction("edit"),
            },
            {
              key: "switch",
              label: t("general.globalSearch.actions.switchOrganization"),
              icon: IconEnter,
              onAction: () => {
                trackAction("switch")();
                switchOrganization(option.organization.id);
              },
            },
          ];
        default:
          return undefined;
      }
    };

    return (
      <>
        <div className={cn("flex items-center gap-2", className)}>
          <AsyncCommand
            ref={ref}
            label={label}
            inputRef={searchInputRef}
            renderOption={(option) => <SearchItemView item={option} isRecent={isShowingRecents} />}
            loadOptions={loadOptions}
            defaultOptions={recentItems}
            resetKey={resetKey}
            size="lg"
            getOptionLabel={(option) => getOptionLabel(option)}
            getOptionValue={(option) => option.id.toString()}
            getOptionKey={getOptionKey}
            getOptionActions={getOptionActions}
            optionActionsLabel={t("general.globalSearch.optionActionsLabel")}
            className="min-w-0 flex-1"
            popoverProps={{
              placement: "bottom",
              className: "w-[460px]",
            }}
            onOptionAction={(option) => {
              if (!option) return;

              const wasRecent = recentItems.some((item) => item.id === option.id);
              const items = lastItemsRef.current.length > 0 ? lastItemsRef.current : recentItems;
              analytics.recordResultSelected({ option, items, wasRecent });

              addRecentItem(option);

              const target = getSearchItemTarget(option);

              if (target) {
                openSearchItemTarget(target);
              }
              analytics.close("selected");
            }}
            placeholder={label}
            listBoxItemProps={{
              className: "h-10",
            }}
          />
          {canUseSuperAdminMode && (
            <SuperAdminModeButton isActive={isSuperAdminMode} onPress={toggleSuperAdminMode} />
          )}
        </div>
        {impersonateTarget && (
          <ImpersonateUserModal
            user={impersonateTarget}
            onOpenChange={(open) => {
              if (!open) setImpersonateTarget(null);
            }}
          />
        )}
      </>
    );
  }
);
